/**
 * Per-network Sui deployment IDs — THE single source of truth (spec Phase 2 item 1).
 * PURE DATA: no import.meta.env / process.env here, so both the Vite client
 * (services/contract-constants.ts) and the Vercel functions (api/_lib/networks.ts)
 * import this same file. Update on (re)publish; Published.toml is the receipt.
 */
export type SuiNetwork = 'testnet' | 'mainnet';

export interface SuiNetworkIds {
  /**
   * CALL TARGET. The latest published version of the package — what a move
   * call must address. **Changes on every upgrade**: `sui client upgrade`
   * issues a new package id, and calls sent to the old one run the old code.
   */
  packageId: string;
  /**
   * TYPE IDENTITY. The package that FIRST published these modules. **Never
   * changes** — not on any upgrade, ever.
   *
   * On Sui a struct's fully-qualified type is pinned to its defining package,
   * so `CustomerShopAccount`, `Voucher`, `Badge` and friends stay typed to
   * this address forever, including objects minted by an upgraded version.
   * Every `StructType` filter and event-type subscription must therefore key
   * off THIS, not packageId.
   *
   * Getting this wrong is silent and total: point the filters at a fresh
   * upgrade's packageId and `getOwnedObjects` matches nothing, so customers
   * read as unregistered and their stamps, rewards and badges vanish from the
   * app while remaining perfectly intact on chain.
   */
  originalPackageId: string;
  /**
   * TYPE IDENTITY for events first defined in the v4 upgrade — currently only
   * `CardCompleteEvent`. **Never changes on a later upgrade.**
   *
   * This exists because `originalPackageId`'s rule cuts both ways: a struct is
   * pinned to the package that FIRST defined it, so a type introduced BY an
   * upgrade belongs to that upgrade, not to the original publish. Filtering
   * `CardCompleteEvent` by `originalPackageId` matches zero events; filtering
   * it by the live `packageId` works until the next upgrade and then silently
   * stops. Both failure modes are invisible — the query returns an empty page,
   * not an error.
   *
   * Empty on testnet: v4 has only ever been published to mainnet, so there is
   * no card-completion event stream there. Consumers must skip when unset.
   */
  cardEventPackageId: string;
  loyaltyProgramId: string;
  badgeRegistryId: string;
  shopRegistryId: string;
  /**
   * The shared StampAuthority object (contracts/STAMP_AUTHORIZATION_DESIGN.md)
   * record_purchase_attested reads its pubkey from. Left '' on both networks
   * on purpose — create_stamp_authority hasn't been called on-chain yet
   * (cutover step 1). Deliberately NOT included in resolveNetworkIds' unfilled
   * check below: the attested path must stay optional (stamp-service.ts falls
   * back to record_purchase) until this is filled in post-creation, unlike the
   * other ids which the app cannot function at all without.
   */
  stampAuthorityId: string;
}

export const SUI_NETWORK_IDS: Record<SuiNetwork, SuiNetworkIds> = {
  // v5 (2026-07-05) — contracts/Published.toml [published.testnet]
  testnet: {
    packageId: '0x2e6ca2fd4913f207ad88f14abe4223958d257f90ee02148304906e7d6e515b16',
    // Equal to packageId until testnet is first upgraded. Do NOT edit on upgrade.
    originalPackageId: '0x2e6ca2fd4913f207ad88f14abe4223958d257f90ee02148304906e7d6e515b16',
    // v4 has never been published to testnet — no CardCompleteEvent stream.
    cardEventPackageId: '',
    loyaltyProgramId: '0xf0b64ab5c72c182a842e21c20d3a20898b4c8fa30a1d25fa50556d0359f3add6',
    badgeRegistryId: '0xffc8d6943d91dfab1e4e489bd6bb35ebfd106923eb673433e4ddc2aa8b991171',
    shopRegistryId: '0xc91d2c62fe685cd8af706c246cae9a34759b98ade3fb5f0e5a929f5534eaa547',
    stampAuthorityId: '', // set after create_stamp_authority is called on testnet
  },
  // Published 2026-07-12, tx 7L4wffNN2zYt89bi2bdp3Wa2kcZ1KJ5Li5ozeeroMY3t —
  // contracts/Published.toml [published.mainnet]. MigrationCap burned same day.
  mainnet: {
    // ⚠️ AFTER `sui client upgrade`, update THIS LINE ONLY to the new package id.
    // v4 2026-08-10 — mints a RewardNFT on card completion (nine earned stamps;
    // the tenth coffee is the free one) alongside the level-up award, and adds
    // per-shop card size as a dynamic field on LoyaltyProgram.
    // tx digest 4YsEGGYpsQEzHKHiLz1cMG17RpZeCc8faZ3zMbtLTp3M.
    // Published.toml [published.mainnet] published-at. Earlier packages stay
    // on chain and remain callable; see the runbook's correction note.
    //
    // ⚠️ CardCompleteEvent is FIRST defined in v4, so its TYPE identity is THIS
    // package, not originalPackageId — the opposite of every other filter in
    // the codebase. See the note in api/_lib/reward-sync.ts before indexing it.
    packageId: '0x703d1f5f947233e0732d5dfb20dd3737555172802553ba4c7b912b3d676ec901',
    // ⚠️ NEVER change this. The 2026-07-12 original publish; it is what every
    // on-chain object of ours is typed to, upgrades included.
    originalPackageId: '0x16b10cb26c38ccc41a5ce0ed1e20c365df2ef3fa696eb6356c5e1518ed480ce2',
    // ⚠️ NEVER change this on a later upgrade. CardCompleteEvent was FIRST
    // defined by v4, so it stays typed to v4 forever — exactly as every
    // pre-v4 type stays typed to originalPackageId.
    cardEventPackageId: '0x703d1f5f947233e0732d5dfb20dd3737555172802553ba4c7b912b3d676ec901',
    loyaltyProgramId: '0x3b91499660e6766c76b56b7b2c3b6434c845f9218bc0f05840328bef0f4750f2',
    badgeRegistryId: '0x37833027c05ae21e6617c46e3ee6164b0f9d3ef54d42620df537a283f50f8ad6',
    shopRegistryId: '0xdd285088984ed33038ebb90383f04b0e8705f78b38e4e290bd985a1f7836e03f',
    // Created 2026-07-29, tx 7AtyKxp7Qd8hn6FgSGb4CKPQB4iUmVEmaeFRuUsF2GTQ.
    // Shared object; its pubkey was verified byte-for-byte against the public
    // half of STAMP_ATTESTATION_KEY in Vercel before being recorded here.
    // NOTE its type origin is the v2 package, not originalPackageId — types
    // FIRST defined in an upgrade belong to that upgrade. Irrelevant for the
    // filters (this is referenced by id, never by StructType), but relevant if
    // a future OBJECT_TYPES entry is ever added for a post-v1 struct.
    stampAuthorityId: '0x03fa7a3df5db27a8607733550152d7ff01761714bb06a9bef042e8d42e40b348',
  },
};

export function resolveNetworkIds(network: string): SuiNetworkIds {
  const ids = SUI_NETWORK_IDS[network as SuiNetwork];
  if (!ids) throw new Error(`Unknown SUI network "${network}" (expected testnet|mainnet)`);
  // stampAuthorityId is deliberately excluded from this check — see the field
  // comment above. It's allowed to be unfilled; the attested path degrades to
  // record_purchase until it's set.
  if (!ids.packageId || !ids.originalPackageId || !ids.loyaltyProgramId || !ids.badgeRegistryId || !ids.shopRegistryId) {
    throw new Error(`SUI network "${network}" selected but its package IDs are unfilled — not published yet?`);
  }
  return ids;
}
